import React, { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import "../styles/main.css";
import { formatEventScore } from "../utils/scoreUtils";
import Logo from "../components/assets/logo-icon.png";

type LeagueName =
  | "Champion's League"
  | "Cutter's League"
  | "Emma Knott's League";
type SearchType = "eventBests" | "userRankings" | "uploadActivity";
type EventType = "bench" | "squat" | "run" | "swim" | "shots";
type Timeframe = "allTime" | "current";
type Gender = "male" | "female" | "all";

interface FilterState {
  leagueName: LeagueName;
  searchType: SearchType;
  selectedEvents: EventType[];
  timeframe: Timeframe;
  gender: Gender;
}

const HomePage: React.FC = () => {
  const assignRanksWithTies = (
    entries: any[],
    options: { ascending?: boolean; byRankAverage?: boolean } = {}
  ): any[] => {
    const { ascending = false, byRankAverage = false } = options;

    const sorted = [...entries].sort((a, b) => {
      const aVal = byRankAverage ? a.rankAverage : a.score;
      const bVal = byRankAverage ? b.rankAverage : b.score;
      return ascending ? aVal - bVal : bVal - aVal;
    });

    const result: any[] = [];
    let currentRank = 1;

    for (let i = 0; i < sorted.length; i++) {
      const entry = sorted[i];
      const prev = sorted[i - 1];

      const sameValue =
        prev &&
        ((byRankAverage && entry.rankAverage === prev.rankAverage) ||
          (!byRankAverage && entry.score === prev.score));

      if (!sameValue) currentRank = i + 1;

      result.push({
        ...entry,
        rank: currentRank,
        tied:
          sameValue ||
          (sorted[i + 1] &&
            ((byRankAverage &&
              entry.rankAverage === sorted[i + 1].rankAverage) ||
              (!byRankAverage && entry.score === sorted[i + 1].score))),
      });
    }

    return result;
  };

  const [filters, setFilters] = useState<FilterState>({
    leagueName: "Champion's League",
    searchType: "userRankings",
    selectedEvents: ["bench", "squat", "run", "swim", "shots"],
    timeframe: "current",
    gender: "all",
  });

  const [entries, setEntries] = useState<any[]>([]); // Replace `any` with a proper type if desired
  const [loading, setLoading] = useState(true);

  const renderEventBestsItem = (entry: any) => (
    <li
      key={`${entry.email}-eventBests`}
      className={`result-card ${
        entry.gender?.toLowerCase() === "male"
          ? "male"
          : entry.gender?.toLowerCase() === "female"
          ? "female"
          : ""
      } ${typeof entry.rank === "number" ? `rank-${entry.rank}` : ""}`}
    >
      <div className="event-bests-row">
        <span className="event-rank-label">
          {typeof entry.rank === "number"
            ? `${entry.rank}${entry.tied ? "T" : ""}`
            : ""}
        </span>
        <span className="event-name">
          {entry.firstName} {entry.lastName}
        </span>
        <span className="age">
          ({entry.age}
          {typeof entry.gender === "string"
            ? entry.gender.charAt(0).toUpperCase()
            : ""}
          )
        </span>
        <span className="event-result">
          {formatEventScore(entry.event, entry.score)}
        </span>
      </div>
    </li>
  );

  const renderUserRankingsItem = (entry: any) => (
    <li
      key={`${entry.email}-userRankings`}
      className={`result-card ${
        entry.gender?.toLowerCase() === "male"
          ? "male"
          : entry.gender?.toLowerCase() === "female"
          ? "female"
          : ""
      } ${typeof entry.rank === "number" ? `rank-${entry.rank}` : ""}`}
    >
      <div className="event-bests-row">
        <span className="event-rank-label">
          {typeof entry.rank === "number"
            ? `${entry.rank}${entry.tied ? "T" : ""}`
            : ""}
        </span>
        <span className="event-name">
          {entry.firstName} {entry.lastName}
        </span>
        <span className="age">
          ({entry.age}
          {typeof entry.gender === "string"
            ? entry.gender.charAt(0).toUpperCase()
            : ""}
          )
        </span>
        <span className="event-result">
          {entry.rankAverage !== undefined
            ? entry.rankAverage.toFixed(1)
            : "N/A"}{" "}
          AR
        </span>
      </div>

      <div className="event-stats-row">
        {filters.selectedEvents.map((event) => {
          const bestEntry = entry.bestResults?.[event];
          const rawScore = bestEntry?.score;
          const rank = entry.eventRanks?.[event];
          const tied = entry.tiedByEvent?.[event];

          let display = "";
          if (bestEntry) {
            if (["run", "swim"].includes(event)) {
              const min = Math.floor(rawScore / 60);
              const sec = Math.floor(rawScore % 60)
                .toString()
                .padStart(2, "0");
              display = min > 0 ? `${min}:${sec}` : `${sec}`;
            } else {
              display = `${Math.round(rawScore)}`;
            }
          }

          return (
            <div key={event} className="event-stat-card">
              <strong>{event.toUpperCase()}</strong>
              <div>
                {display}{" "}
                {rank && (
                  <span className="rank-display">
                    <span
                      className={`event-rank ${
                        entry.penalizedEvents?.[event] ? "penalty-rank" : ""
                      }`}
                    >
                      R{rank}
                      {tied ? "T" : ""}
                    </span>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </li>
  );

  const renderUploadActivityItem = (entry: any) => (
    <li
      key={`${entry.email}-uploadActivity-${entry.event}-${entry.year}-${entry.month}-${entry.day}`}
      className={`result-card ${
        entry.gender?.toLowerCase() === "male"
          ? "male"
          : entry.gender?.toLowerCase() === "female"
          ? "female"
          : ""
      }`}
    >
      <div className="event-bests-row">
        <span className="event-name">
          {entry.firstName} {entry.lastName}
        </span>
        <span className="age">
          {typeof entry.event === "string" ? entry.event.toUpperCase() : ""} •{" "}
          {entry.age}
          {typeof entry.gender === "string"
            ? entry.gender.charAt(0).toUpperCase()
            : ""}{" "}
          • {entry.month}/{entry.day}
        </span>
        <span className="event-result">
          {formatEventScore(entry.event, entry.score)}
        </span>
      </div>
    </li>
  );

  useEffect(() => {
    const fetchEntries = async () => {
      setLoading(true);

      const leagueId = filters.leagueName.toLowerCase().replace(/ /g, "-");
      const leagueRef = collection(db, "leagues");
      const leagueQuery = query(leagueRef, where("__name__", "==", leagueId));
      const leagueSnapshot = await getDocs(leagueQuery);

      let leagueAthletes: string[] = [];
      if (!leagueSnapshot.empty) {
        const leagueData = leagueSnapshot.docs[0].data();
        leagueAthletes = leagueData.athletesArray || [];
      }

      const currentYear = new Date().getFullYear();
      const isCurrentTimeframe = filters.timeframe === "current";

      try {
        if (filters.searchType === "userRankings") {
          const athletesSnapshot = await getDocs(collection(db, "athletes"));
          const allAthletes = athletesSnapshot.docs
            .map((doc) => doc.data())
            .filter((athlete) => {
              const genderMatch =
                filters.gender === "all"
                  ? true
                  : athlete.gender.toLowerCase() === filters.gender;
              const leagueMatch =
                !leagueAthletes.length ||
                leagueAthletes.includes(athlete.email);
              return genderMatch && leagueMatch;
            });

          const entriesSnapshot = await getDocs(collection(db, "entries"));
          const allEntries = entriesSnapshot.docs.map((doc) => doc.data());

          const bestResultsByAthleteEvent: { [key: string]: any } = {};

          for (const entry of allEntries) {
            const key = `${entry.email}_${entry.event}`;
            if (
              !filters.selectedEvents.includes(entry.event) ||
              (filters.gender !== "all" &&
                entry.gender.toLowerCase() !== filters.gender)
            ) {
              continue;
            }
            if (isCurrentTimeframe && entry.year !== currentYear) continue;

            if (!bestResultsByAthleteEvent[key]) {
              bestResultsByAthleteEvent[key] = entry;
            } else {
              const current = bestResultsByAthleteEvent[key];
              const isRunOrSwim = ["run", "swim"].includes(entry.event);
              const isBetter = isRunOrSwim
                ? entry.score < current.score
                : entry.score > current.score;

              if (isBetter) {
                bestResultsByAthleteEvent[key] = entry;
              }
            }
          }

          const ranksByEvent: {
            [event: string]: { email: string; rank: number; tied?: boolean }[];
          } = {};

          for (const event of filters.selectedEvents) {
            const relevantEntries = Object.values(
              bestResultsByAthleteEvent
            ).filter((e) => e.event === event);

            ranksByEvent[event] = assignRanksWithTies(relevantEntries, {
              ascending: ["run", "swim"].includes(event),
            }).map((entry) => ({
              email: entry.email,
              rank: entry.rank,
              tied: entry.tied,
            }));
          }

          const athleteRanks = allAthletes.map((athlete) => {
            let total = 0;
            let count = 0;

            const bestResults: { [event: string]: any } = {};
            const eventRanks: { [event: string]: number | null } = {};
            const penalizedEvents: { [event: string]: boolean } = {};
            const tiedByEvent: { [event: string]: boolean } = {};

            for (const event of filters.selectedEvents) {
              const key = `${athlete.email}_${event}`;
              if (bestResultsByAthleteEvent[key]) {
                bestResults[event] = bestResultsByAthleteEvent[key];
              }
            }

            for (const event of filters.selectedEvents) {
              const ranks = ranksByEvent[event] || [];
              const found = ranks.find((r) => r.email === athlete.email);
              eventRanks[event] = found ? found.rank : ranks.length + 2;
              tiedByEvent[event] = found?.tied ?? false;
              penalizedEvents[event] = !found;

              total += eventRanks[event] as number;
              count++;
            }

            const average = total / count;

            const birth = athlete.birthday?.toDate?.() || athlete.birthday;
            const age = birth
              ? Math.floor(
                  (Date.now() - new Date(birth).getTime()) /
                    (365.25 * 24 * 60 * 60 * 1000)
                )
              : null;

            return {
              ...athlete,
              age,
              rankAverage: Math.round(average * 10) / 10,
              bestResults,
              eventRanks,
              penalizedEvents,
              tiedByEvent,
            };
          });

          const rankedUserRanks = assignRanksWithTies(athleteRanks, {
            ascending: true,
            byRankAverage: true,
          });
          setEntries(rankedUserRanks);
        } else if (filters.searchType === "uploadActivity") {
          const entriesSnapshot = await getDocs(collection(db, "entries"));
          const allEntries = entriesSnapshot.docs.map((doc) => doc.data());

          const filtered = allEntries.filter((entry) => {
            const genderMatch =
              filters.gender === "all" ||
              entry.gender.toLowerCase() === filters.gender;
            const eventMatch = filters.selectedEvents.includes(entry.event);
            const timeframeMatch =
              !isCurrentTimeframe || entry.year === currentYear;
            const leagueMatch =
              !leagueAthletes.length || leagueAthletes.includes(entry.email);
            return genderMatch && eventMatch && timeframeMatch && leagueMatch;
          });

          const sortedByDate = filtered.sort((a, b) => {
            const dateA = new Date(a.year, a.month - 1, a.day);
            const dateB = new Date(b.year, b.month - 1, b.day);
            return dateB.getTime() - dateA.getTime(); // descending
          });

          setEntries(sortedByDate);
        } else {
          const entriesRef = collection(db, "entries");

          const baseFilters = [
            ...(filters.gender !== "all"
              ? [
                  where(
                    "gender",
                    "==",
                    filters.gender.charAt(0).toUpperCase() +
                      filters.gender.slice(1)
                  ),
                ]
              : []),
            where("event", "in", filters.selectedEvents),
          ];

          const q = query(
            entriesRef,
            ...baseFilters,
            orderBy(
              "score",
              filters.searchType === "eventBests"
                ? ["run", "swim"].includes(filters.selectedEvents[0])
                  ? "asc"
                  : "desc"
                : "asc"
            )
          );

          const snapshot = await getDocs(q);
          const docs = snapshot.docs
            .map((doc) => doc.data())
            .filter((entry) => {
              const leagueMatch =
                !leagueAthletes.length || leagueAthletes.includes(entry.email);
              return (
                (isCurrentTimeframe ? entry.year === currentYear : true) &&
                leagueMatch
              );
            });

          const bestEntriesByEmail: { [email: string]: any } = {};

          for (const entry of docs) {
            const key = entry.email;
            if (!bestEntriesByEmail[key]) {
              bestEntriesByEmail[key] = entry;
            } else {
              const currentBest = bestEntriesByEmail[key];
              const isRunOrSwim = ["run", "swim"].includes(entry.event);
              const isBetter = isRunOrSwim
                ? entry.score < currentBest.score
                : entry.score > currentBest.score;

              if (isBetter) {
                bestEntriesByEmail[key] = entry;
              }
            }
          }

          const rankedEntries = assignRanksWithTies(
            Object.values(bestEntriesByEmail),
            { ascending: ["run", "swim"].includes(filters.selectedEvents[0]) }
          );

          setEntries(rankedEntries);
        }
      } catch (err) {
        console.error("Error fetching leaderboard:", err);
        setEntries([]);
      }

      setLoading(false);
    };

    fetchEntries();
  }, [filters]);

  useEffect(() => {
    if (
      filters.searchType === "eventBests" &&
      filters.selectedEvents.length > 1
    ) {
      setFilters((prev) => ({
        ...prev,
        selectedEvents: [prev.selectedEvents[0]],
      }));
    }
  }, [filters.searchType, filters.selectedEvents]);

  return (
    <div className="home-page-container">
      {/* Navigation Bar */}
      <nav className="navbar">
        <div className="navbar-content">
          <img className="site-logo" src={Logo} alt="Cutter Games Logo" />
          <div className="site-header">
            <h1 className="site-title">Cutter Games</h1>
          </div>
          <ul className="nav-links">
            <li>
              <a href="/" className="nav-link">
                Home
              </a>
            </li>
            <li>
              <a href="/about" className="nav-link">
                About
              </a>
            </li>
            <li>
              <a href="/upload" className="nav-link">
                Upload
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Query Builder Section */}
      <section className="query-builder-section">
        <div className="row">
          <div className="filter-label">League</div>
          <div className="filter-label">Search Type</div>
        </div>
        <div className="row gap">
          <div className="filter-group">
            <select
              className="filter-button"
              value={filters.leagueName}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  leagueName: e.target.value as LeagueName,
                }))
              }
            >
              {[
                "Champion's League",
                "Cutter's League",
                "Emma Knott's League",
              ].map((league) => (
                <option key={league} value={league}>
                  {league}
                </option>
              ))}
            </select>
          </div>
          {/* Search Type Toggle */}
          <div className="filter-group search-type">
            <select
              className="filter-button"
              value={filters.searchType}
              onChange={(e) =>
                setFilters((prev) => {
                  const updatedEvents =
                    e.target.value === "userRankings" ||
                    e.target.value === "uploadActivity"
                      ? ["bench", "squat", "run", "swim", "shots"]
                      : prev.selectedEvents;

                  return {
                    ...prev,
                    searchType: e.target.value as SearchType,
                    selectedEvents: updatedEvents as EventType[],
                  };
                })
              }
            >
              <option value="userRankings">User Rankings</option>
              <option value="eventBests">Event Bests</option>
              <option value="uploadActivity">Upload Activity</option>
            </select>
          </div>
        </div>

        {/* Event Selection for eventBests */}
        {filters.searchType === "eventBests" && (
          <>
            <div className="row">
              <div className="filter-label">Event Selection</div>
            </div>
            <div className="row gap">
              <div className="filter-group event-select">
                <select
                  className="filter-button"
                  value={filters.selectedEvents[0]}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      selectedEvents: [e.target.value as EventType],
                    }))
                  }
                >
                  {["bench", "squat", "run", "swim", "shots"].map((event) => (
                    <option key={event} value={event}>
                      {event.charAt(0).toUpperCase() + event.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* Event Selection for not eventBests */}
        {filters.searchType !== "eventBests" && (
          <>
            <div className="row">
              <div className="filter-label">Exclude Events</div>
            </div>
            <div className="row gap">
              <div className="filter-group event-select">
                {["bench", "squat", "run", "swim", "shots"].map((event) => {
                  const selected = filters.selectedEvents.includes(
                    event as EventType
                  );

                  return (
                    <button
                      key={event}
                      className={`filter-button ${selected ? "" : "active"}`}
                      onClick={() => {
                        setFilters((prev) => ({
                          ...prev,
                          selectedEvents: selected
                            ? prev.selectedEvents.filter((e) => e !== event)
                            : [...prev.selectedEvents, event as EventType],
                        }));
                      }}
                    >
                      {event.charAt(0).toUpperCase() + event.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <div className="row">
          <div className="filter-label">Timeframe</div>
          <div className="filter-label">Gender</div>
        </div>

        <div className="row">
          {/* Timeframe Filter */}
          <div className="filter-group">
            <select
              className="filter-button"
              value={filters.timeframe}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  timeframe: e.target.value as Timeframe,
                }))
              }
            >
              <option value="allTime">All-Time</option>
              <option value="current">Current</option>
            </select>
          </div>
          {/* Gender Filter */}
          <div className="filter-group">
            <select
              className="filter-button"
              value={filters.gender}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  gender: e.target.value as Gender,
                }))
              }
            >
              <option value="all">All</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        </div>
      </section>

      {/* Results List Section */}
      <section className="results-list-section">
        <h2 className="section-title">
          {`${filters.leagueName.split(" ").slice(0, -1).join(" ")}`}{" "}
          {filters.searchType === "eventBests"
            ? `${
                filters.selectedEvents[0].charAt(0).toUpperCase() +
                filters.selectedEvents[0].slice(1)
              } Bests`
            : filters.searchType === "userRankings"
            ? "Leaderboard"
            : "Upload Activity"}
          {filters.gender !== "all"
            ? ` ${
                filters.gender.charAt(0).toUpperCase() + filters.gender.slice(1)
              }`
            : ""}
          {filters.timeframe === "current"
            ? ` ${new Date().getFullYear()}`
            : ""}
        </h2>

        {loading ? (
          <p className="loading-text">Loading...</p>
        ) : entries.length === 0 ? (
          <p>No results found.</p>
        ) : (
          <ul className="leaderboard-list">
            {filters.searchType === "eventBests" &&
              entries.map(renderEventBestsItem)}
            {filters.searchType === "userRankings" &&
              entries.map(renderUserRankingsItem)}
            {filters.searchType === "uploadActivity" &&
              entries.map(renderUploadActivityItem)}
          </ul>
        )}
      </section>
    </div>
  );
};

export default HomePage;
